import * as Dfns from 'date-fns/fp';
import { useMemo } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Bar, BarChart, Line, LineChart, ReferenceLine, Tooltip } from 'recharts';
import { ChartXAxis, ChartYAxis, ChartGrid } from '@Components/Charts/chart-presets';
import { currencyFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { Neutral600 } from '@src/utils/colors';
import { PacingDetailSeries, Simplicity } from '../pacing-constants';
import { NON_MONETARY_GOALS, goalNameToKey, isPacingRatioGoalType, today } from '../pacingUtils';
import { PacingTooltip } from './PacingTooltip';

// One cumulative chart point per date; the four spend fields are null until the date qualifies.
interface CumulativeDataPoint {
  cumulativeClearedSimpleIdealSpend: null | number;
  cumulativeClearedSimpleSpend: null | number;
  cumulativeProjectedAdjustedSpend: null | number;
  cumulativeProjectedSimpleSpend: null | number;
  date: string;
  goalValue: number;
}

interface ModalCumulativeAndDailyGraphProps {
  data: Record<string, any>;
  start: string;
  end: string;
  showCumulative: boolean;
  simplicity: string;
  goalName: string;
}

const yesterday = Dfns.format('yyyy-MM-dd', Dfns.subDays(1, new Date(`${today}T00:00:00`)));

const formatMetricValue = (
  value: number,
  isNonMonetaryMetric: boolean,
  isMetricGoal: boolean,
  currencyFractionDigits: number
) => {
  if (isNonMonetaryMetric) {
    return value.toLocaleString('en-us', { maximumFractionDigits: 2 });
  }
  if (isMetricGoal) {
    return `$${value.toLocaleString('en-us', { maximumFractionDigits: 2 })}`;
  }
  return currencyFormatter.format(value, currencyFractionDigits);
};

const HorizontalCustomLabelBudget = ({
  // recharts injects viewBox at render, so it's optional at the JSX call site.
  viewBox = { x: 0, y: 0 },
  value,
  isNonMonetaryMetric,
  isMetricGoal
}: {
  viewBox?: { x: number; y: number };
  value: number | null;
  isNonMonetaryMetric: boolean;
  isMetricGoal: boolean;
}) => {
  const { x, y } = viewBox;
  const displayValue =
    value != null ? formatMetricValue(value, isNonMonetaryMetric, isMetricGoal, 1) : '—';
  return (
    <text x={x} y={y} className='pacing-ref-label' fill={PacingDetailSeries.budget.color}>
      <tspan x={x - 36} dy='-.2em'>
        {displayValue}
      </tspan>
      <tspan x={x - 36} dy='1em'>
        Budget
      </tspan>
    </text>
  );
};

const VerticalCustomLabelSpend = ({
  // recharts injects viewBox at render, so it's optional at the JSX call site.
  viewBox = { x: 0, y: 0 },
  goal,
  deliveryTotal
}: {
  viewBox?: { x: number; y: number };
  goal: number;
  deliveryTotal: number;
}) => {
  const { x, y } = viewBox;

  const percentage =
    goal && Number.isFinite(deliveryTotal) ? ((deliveryTotal / goal) * 100).toFixed(0) : '0';

  return (
    <text x={x + 10} y={y + 20} className='pacing-yesterday-label'>
      <tspan x={x + 10} dy='-.2em'>
        Yesterday
      </tspan>
      <tspan x={x + 10} dy='1.2em'>
        {`${percentage}% through the time period`}
      </tspan>
    </text>
  );
};

export const ModalCumulativeAndDailyGraph = ({
  data,
  start,
  end,
  showCumulative,
  simplicity,
  goalName: propsGoalName
}: ModalCumulativeAndDailyGraphProps) => {
  const goalName = goalNameToKey(propsGoalName);
  const isAdjusted = simplicity === Simplicity.ADJUSTED;

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + 5,
    yAxisWidthModifier: x => x + 3
  });

  const { deliveryTotal, goalValue, goal, barActual, barIdeal } = useMemo(
    () => ({
      deliveryTotal: data.simpleDeliveryTotal?.[goalName],
      // A targetless (ratio-goalless) node omits goalValue/booked — guard so the modal still opens.
      goalValue: data.goalValue?.[goalName],
      goal: data.booked?.[goalName],
      barActual: isAdjusted ? data.barAdjustedActual : data.barSimpleActual,
      barIdeal: isAdjusted ? data.barAdjustedIdeal : data.barSimpleIdeal
    }),
    [data, goalName, isAdjusted]
  );

  const isMetricGoal = isPacingRatioGoalType(propsGoalName);
  const isNonMonetaryMetric = NON_MONETARY_GOALS.includes(propsGoalName);

  const cumulativeGraphDataAdjusted = useMemo(() => {
    const cumulativeByDate = isAdjusted
      ? data.adjustedProjectedCumulativeByDate
      : data.simpleProjectedCumulativeByDate;

    // A targetless (ratio-goalless) node may omit the cumulative maps; guard so Object.keys doesn't
    // throw (R.keys tolerated undefined; Object.keys does not).
    return Object.keys(cumulativeByDate ?? {})
      .sort()
      .filter(
        (date: string) => new Date(date) >= new Date(start) && new Date(date) <= new Date(end)
      )
      .reduce(
        (acc: { points: CumulativeDataPoint[]; cumulativeIdeal: number }, date: string) => {
          const cumulativeValue = cumulativeByDate[date]?.[goalName] ?? null;
          const idealForDate = barIdeal?.[date]?.[goalName] ?? 0;
          const cumulativeIdeal = acc.cumulativeIdeal + idealForDate;

          const dataPoint: CumulativeDataPoint = {
            cumulativeClearedSimpleIdealSpend: null,
            cumulativeClearedSimpleSpend: null,
            cumulativeProjectedAdjustedSpend: null,
            cumulativeProjectedSimpleSpend: null,
            date,
            goalValue
          };

          // `date` and `yesterday` are both yyyy-MM-dd, so they compare directly (lexicographic
          // order preserves yyyy-MM-dd chronology).
          if (date < yesterday) {
            dataPoint.cumulativeClearedSimpleSpend = cumulativeValue;
            if (!isMetricGoal) {
              dataPoint.cumulativeClearedSimpleIdealSpend = cumulativeIdeal;
            }
          } else if (date === yesterday) {
            dataPoint.cumulativeClearedSimpleSpend = cumulativeValue;
            if (!isMetricGoal) {
              dataPoint.cumulativeClearedSimpleIdealSpend = cumulativeIdeal;
              dataPoint.cumulativeProjectedSimpleSpend = cumulativeValue;
              dataPoint.cumulativeProjectedAdjustedSpend = cumulativeIdeal;
            }
          } else if (!isMetricGoal) {
            dataPoint.cumulativeProjectedSimpleSpend = cumulativeValue;
            dataPoint.cumulativeProjectedAdjustedSpend = cumulativeIdeal;
          }
          return { points: [...acc.points, dataPoint], cumulativeIdeal };
        },
        { points: [], cumulativeIdeal: 0 }
      ).points;
  }, [data, start, end, goalValue, goalName, isMetricGoal, isAdjusted, barIdeal]);

  const barData = useMemo(() => {
    // Use LA-today (the data's cutoff) so today's partial day renders as projected, not a cleared bar.
    const cutoffDate = today;
    // Guard the delivery map: a targetless node may omit it, and Object.keys(undefined) throws.
    const deliveryByDate = data.simpleDeliveryByDate ?? {};

    return Object.keys(deliveryByDate)
      .sort()
      .filter(
        (date: string) => new Date(date) >= new Date(start) && new Date(date) <= new Date(end)
      )
      .map((date: string) => {
        const dataPoint: {
          clearedSimpleIdealSpend: null | number;
          clearedSimpleSpend: null | number;
          projectedAdjustedSpend: null | number;
          projectedSimpleSpend: null | number;
          date: string;
        } = {
          clearedSimpleIdealSpend: null,
          clearedSimpleSpend: null,
          projectedAdjustedSpend: null,
          projectedSimpleSpend: null,
          date
        };
        const formattedCurrent = new Date(date).toISOString().split('T')[0];
        const actualForDate = barActual?.[date];
        const idealForDate = barIdeal?.[date];

        if (formattedCurrent < cutoffDate) {
          dataPoint.clearedSimpleSpend = actualForDate?.[goalName] ?? null;
          if (!isMetricGoal) {
            dataPoint.clearedSimpleIdealSpend = idealForDate?.[goalName] ?? null;
          }
        } else if (!isMetricGoal) {
          dataPoint.projectedSimpleSpend = actualForDate?.[goalName] ?? null;
          dataPoint.projectedAdjustedSpend = idealForDate?.[goalName] ?? null;
        }

        return dataPoint;
      });
  }, [data, start, end, barActual, barIdeal, isMetricGoal, goalName]);

  const tickSize = useMemo(() => {
    const lastObject = cumulativeGraphDataAdjusted[cumulativeGraphDataAdjusted.length - 1] || {};
    const values = [
      lastObject.cumulativeClearedSimpleSpend || 0,
      lastObject.cumulativeProjectedSimpleSpend || 0,
      lastObject.cumulativeClearedSimpleIdealSpend || 0,
      lastObject.cumulativeProjectedAdjustedSpend || 0
    ];
    const largestValue = Math.max(...values);
    return largestValue / 5;
  }, [cumulativeGraphDataAdjusted]);

  const formatYAxis = (value, targetValue) => {
    if (value == null) {
      return '';
    }
    if (Math.abs(value - targetValue) < tickSize / 2) {
      return '';
    }
    return formatMetricValue(value, isNonMonetaryMetric, isMetricGoal, 0);
  };

  return (
    <AutoSizer
      renderProp={({ width = 0, height = 0 }) =>
        showCumulative ? (
          <LineChart
            data={cumulativeGraphDataAdjusted}
            height={height}
            width={width}
            ref={setChartRef}
            {...{
              overflow: 'visible'
            }}
          >
            <Tooltip content={<PacingTooltip goalName={propsGoalName} />} />
            <ChartGrid strokeDasharray='0' horizontal vertical={false} />
            <ChartXAxis
              dataKey='date'
              tickFormatter={val => Dfns.format('M/dd', Dfns.parseISO(val))}
              height={xAxisHeight}
            />
            <ChartYAxis tickFormatter={value => formatYAxis(value, goal)} width={yAxisWidth} />
            <Line
              dot={false}
              type='monotone'
              dataKey='cumulativeClearedSimpleSpend'
              stroke={PacingDetailSeries.cumulativeClearedSimpleSpend.color}
              isAnimationActive={false}
            />
            <Line
              dot={false}
              type='monotone'
              dataKey='cumulativeProjectedSimpleSpend'
              stroke={PacingDetailSeries.cumulativeProjectedSimpleSpend.color}
              strokeDasharray='4'
              isAnimationActive={false}
            />
            <Line
              dot={false}
              type='monotone'
              dataKey='cumulativeClearedSimpleIdealSpend'
              stroke={PacingDetailSeries.cumulativeClearedSimpleIdealSpend.color}
              isAnimationActive={false}
            />
            <Line
              dot={false}
              type='monotone'
              dataKey='cumulativeProjectedAdjustedSpend'
              stroke={PacingDetailSeries.cumulativeProjectedAdjustedSpend.color}
              strokeDasharray='4'
              isAnimationActive={false}
            />
            <ReferenceLine
              x={yesterday}
              label={<VerticalCustomLabelSpend goal={goal} deliveryTotal={deliveryTotal} />}
              stroke={Neutral600}
            />
            <ReferenceLine
              y={goal}
              label={
                <HorizontalCustomLabelBudget
                  value={goal}
                  isNonMonetaryMetric={isNonMonetaryMetric}
                  isMetricGoal={isMetricGoal}
                />
              }
              stroke={PacingDetailSeries.budget.color}
              strokeWidth={3}
            />
          </LineChart>
        ) : (
          <BarChart
            data={barData}
            height={height}
            width={width}
            {...{
              overflow: 'visible'
            }}
          >
            <Tooltip content={<PacingTooltip goalName={propsGoalName} />} />
            <ChartYAxis
              tickFormatter={val =>
                val != null ? formatMetricValue(val, isNonMonetaryMetric, isMetricGoal, 0) : ''
              }
            />
            <ChartXAxis
              dataKey='date'
              tickFormatter={val => Dfns.format('M/dd', Dfns.parseISO(val))}
            />
            <Bar dataKey='clearedSimpleSpend' fill={PacingDetailSeries.clearedSimpleSpend.color} />
            <Bar
              dataKey='clearedSimpleIdealSpend'
              fill={PacingDetailSeries.clearedSimpleIdealSpend.color}
            />
            <Bar
              dataKey='projectedSimpleSpend'
              fill={PacingDetailSeries.projectedSimpleSpend.color}
            />
            <Bar
              dataKey='projectedAdjustedSpend'
              fill={PacingDetailSeries.projectedAdjustedSpend.color}
            />
            <ReferenceLine
              x={yesterday}
              label={<VerticalCustomLabelSpend goal={goal} deliveryTotal={deliveryTotal} />}
              stroke={Neutral600}
            />
          </BarChart>
        )
      }
    />
  );
};
